import React from "react";
import { Col, Row, Button } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const amenitiesList = [
  "swimming_pool",
  "gas_connection",
  "ro",
  "club_house",
  "basketball_court",
  "tennis_court",
  "gym",
  "indoor_games",
  "child_play_area",
  "hospital_near_by",
  "mall_near_by",
  "market_near_by",
  "school_near_by",
];

const amenitiesLabels = {
  swimming_pool: "Swimming Pool",
  gas_connection: "Gas Connection",
  ro: "RO",
  club_house: "Club House",
  basketball_court: "Basketball Court",
  tennis_court: "Tennis Court",
  gym: "Gym",
  indoor_games: "Indoor Games",
  child_play_area: "Child Play Area",
  hospital_near_by: "Hospital Near By",
  mall_near_by: "Mall Near By",
  market_near_by: "Market Near By",
  school_near_by: "School Near By",
};

const Step4Features = ({ form, setForm, nextStep, prevStep }) => {
  const initialValues = {
    bedrooms: form?.structure?.bed_rooms || "",
    bathrooms: form?.structure?.bath_rooms || "",
    rooms: form?.structure?.rooms || "",
    tags: form?.tags || "",
    pin_code: form?.pin_code || "",
    address: form?.address || "",
    ...amenitiesList.reduce((acc, key) => {
      acc[key] = form?.feature?.[key] || false;
      return acc;
    }, {}),
  };

  const validationSchema = Yup.object({
    bedrooms: Yup.number().nullable(),
    bathrooms: Yup.number().nullable(),
    rooms: Yup.number().nullable(),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const features = amenitiesList.reduce((acc, key) => {
      acc[key] = values[key];
      return acc;
    }, {});

    const otherValues = { ...values };
    amenitiesList.forEach((key) => delete otherValues[key]);

    const structure = {
      id: form?.structure?.id || null,
      bed_rooms: Number(otherValues.bedrooms || 0),
      bath_rooms: Number(otherValues.bathrooms || 0),
      rooms: Number(otherValues.rooms || 0),
    };

    delete otherValues.bedrooms;
    delete otherValues.bathrooms;
    delete otherValues.rooms;

    setForm((prev) => ({
      ...prev,
      ...otherValues,
      feature: features,
      structure,
    }));

    setSubmitting(false);
    if (nextStep) nextStep();
  };

  const handlePrevious = (values) => {
    const features = amenitiesList.reduce((acc, key) => {
      acc[key] = values[key];
      return acc;
    }, {});

    const otherValues = { ...values };
    amenitiesList.forEach((key) => delete otherValues[key]);

    const structure = {
      id: form?.structure?.id || null,
      bed_rooms: Number(otherValues.bedrooms || 0),
      bath_rooms: Number(otherValues.bathrooms || 0),
      rooms: Number(otherValues.rooms || 0),
    };

    delete otherValues.bedrooms;
    delete otherValues.bathrooms;
    delete otherValues.rooms;

    setForm((prev) => ({
      ...prev,
      ...otherValues,
      feature: features,
      structure,
    }));

    if (prevStep) prevStep();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => (
        <Form className="p-4 bg-light rounded shadow-sm">
          <div className="mb-4 fs-3 text-uppercase fw-bold">Internal Structure</div>
          <Row className="mb-4 row-gap-2 mt-4">
            {[
              { label: "Bedrooms", name: "bedrooms" },
              { label: "Bathrooms", name: "bathrooms" },
              { label: "Rooms", name: "rooms" },
              { label: "Tags", name: "tags", type: "text" },
              { label: "Pin Code", name: "pin_code", type: "text" },
              { label: "Address", name: "address", type: "text" },
            ].map(({ label, name, type = "number" }, index) => (
              <Col md={4} className="mb-3" key={index}>
                <h5 htmlFor={name} className="mb-2">{label}</h5>
                <Field
                  type={type}
                  className="form-control bg-white h-10"
                  id={name}
                  placeholder={`Enter ${label}`}
                  name={name}
                />
                <ErrorMessage name={name} component="div" className="text-danger small" />
              </Col>
            ))}
          </Row>

          <div className="mb-4 fs-3 text-uppercase fw-bold">Amenities</div>
          <Row className="gy-2">
            {amenitiesList.map((key, index) => (
              <Col md={2} key={index} className="mb-2 d-flex flex-wrap g-2">
                <div className="form-check d-flex">
                  <Field
                    className="form-check-input mr-2"
                    type="checkbox"
                    id={key}
                    name={key}
                  />
                  <label className="form-check-label" htmlFor={key}>
                    {amenitiesLabels[key]}
                  </label>
                </div>
              </Col>
            ))}
          </Row>

          <div className="d-flex justify-content-between mt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => handlePrevious(values)}
            >
              Previous
            </Button>
            <div>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                style={{ marginRight: "1rem" }}
              >
                {isSubmitting ? "Submitting..." : "Skip"}
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Next"}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Step4Features;
